/**
 * Inventory Blocking Times Service
 *
 * Data access layer for inventory_blocking_times table.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export interface InventoryBlockingTime {
  id: string;
  inventory_item_id: string;
  blocked_from: string;
  blocked_until: string;
  blocking_reason: string;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InventoryBlockingTimeInsert {
  inventory_item_id: string;
  blocked_from: string;
  blocked_until: string;
  blocking_reason: string;
  notes?: string | null;
  created_by_user_id?: string | null;
}

export interface InventoryBlockingTimeUpdate {
  blocked_from?: string;
  blocked_until?: string;
  blocking_reason?: string;
  notes?: string | null;
}

/**
 * Fetch all blocking times for an inventory item
 */
export async function getBlockingTimesByInventoryItemId(inventoryItemId: string) {
  try {
    const { data, error } = await supabase
      .from('inventory_blocking_times')
      .select('*')
      .eq('inventory_item_id', inventoryItemId)
      .order('blocked_from', { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new blocking time
 */
export async function createBlockingTime(blockingTime: InventoryBlockingTimeInsert) {
  try {
    const { data, error } = await supabase
      .from('inventory_blocking_times')
      .insert(blockingTime)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a blocking time
 */
export async function updateBlockingTime(id: string, updates: InventoryBlockingTimeUpdate) {
  try {
    const { data, error } = await supabase
      .from('inventory_blocking_times')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a blocking time
 */
export async function deleteBlockingTime(id: string) {
  try {
    const { error } = await supabase
      .from('inventory_blocking_times')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
